import { createFormDataRequest } from "@/helpers/FormHelpers";
import Company, { CompanyFiltersSend } from "@/interfaces/Company";
import CompanyCourse, {
  CompaniesCoursesFiltersSend,
  CompanyCourseFormSend,
} from "@/interfaces/CompanyCourse";
import { IPaginateResponse } from "@/interfaces/Fetch";
import api from "@/services/api";

export const companiesCoursesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCompaniesCourses: build.query<
      IPaginateResponse<CompanyCourse>,
      CompaniesCoursesFiltersSend
    >({
      query: (data) => {
        const params = createFormDataRequest(data);
        return {
          url: `/general`,
          params,
        };
      },
      providesTags: (_result, _error, _arg) => [
        { type: "CompanyCourses", id: "LIST" },
      ],
    }),
    getCompanyCourses: build.query<
      IPaginateResponse<CompanyCourse>,
      [string, CompanyFiltersSend]
    >({
      query: ([id, data]) => {
        const params = createFormDataRequest(data);
        return {
          url: `/companies/${id}/courses`,
          params,
        };
      },
      providesTags: (result, _error, [companyId]) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "CompanyCourses" as const,
                companyId,
                id,
              })),
              { type: "CompanyCourses", companyId, id: "LIST" },
            ]
          : [],
    }),
    getCompanyCourse: build.query<CompanyCourse, [string, string]>({
      query: ([companyId, id]) => `/companies/${companyId}/courses/${id}`,
      providesTags: (_result, _error, [companyId, id]) => [
        { type: "CompanyCourses", companyId, id },
      ],
    }),
    storeCompanyCourse: build.mutation<
      Company,
      [string, CompanyCourseFormSend]
    >({
      query: ([id, body]) => ({
        url: `/companies/${id}/courses`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, [companyId]) => [
        { type: "CompanyCourses", companyId, id: "LIST" },
      ],
    }),
    updateCompanyCourse: build.mutation<
      Company,
      [string, string, Partial<CompanyCourseFormSend>]
    >({
      query: ([companyId, id, body]) => ({
        url: `/companies/${companyId}/courses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, [companyId, id]) => [
        { type: "CompanyCourses", id: "LIST" },
        { type: "CompanyCourses", id },
        { type: "CompanyCourseCommissions", companyId, courseId: id },
      ],
    }),
    deleteCompanyCourse: build.mutation<void, [string, string]>({
      query: ([companyId, id]) => ({
        url: `/companies/${companyId}/courses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, [companyId]) => [
        { type: "CompanyCourses", companyId, id: "LIST" },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCompanyCourseQuery,
  useStoreCompanyCourseMutation,
  useGetCompanyCoursesQuery,
  useLazyGetCompanyCoursesQuery,
  useGetCompaniesCoursesQuery,
  useLazyGetCompaniesCoursesQuery,
  useUpdateCompanyCourseMutation,
  useDeleteCompanyCourseMutation,
} = companiesCoursesApi;
